import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CERTIFICATE_STATUS, CERTIFICATE_TYPE, PROFILE_INFORMATION } from "../../data/NewApplicantsConstants";

import { BULK_REGISTRATION_STATUS } from "../../data/BulkRegistrationConstants";
import moment from "moment";
import { incompleteApplicationsService } from "../../services/incompleteApplicationsService";

// initial state
const initialState = {
    loading: false,
    error: null,
    data: {
        current_page: 1,
        page_limit: 10,
        total_pages: 1,
    },
    profileData: {},
    certificateType: CERTIFICATE_TYPE.ALL,
    certificateStatus: CERTIFICATE_STATUS.ALL,
    profileDetailsType: PROFILE_INFORMATION.BASIC_INFORMATION,
    startDate: null,
    endDate: null,
    search: "",
    bulkRegistrationStatus: BULK_REGISTRATION_STATUS.QR_CODE,
    applicationStatus: null,
    certificateTSTCStatus: CERTIFICATE_STATUS.ALL,
    userId: "",
};

export const fetchIncompleteApplicantions = createAsyncThunk(
    "applicants/fetchIncompleteApplicantions",
    async (
        {
            page,
            limit,
            start_date,
            end_date,
            type,
            full_name,
            registered_from,
            registered_to,
            board_approval_from,
            board_approval_to,
            email,
            registration_no,
            filter_status,
            technology_field,
            identity_number,
            payment_from,
            payment_to,
            search,
            status,
            assessment_from,
            assessment_to,
        },
        thunkAPI
    ) => {
        try {
            const response = await incompleteApplicationsService.getincompleteApplications({
                page,
                limit,
                type,
                start_date: start_date ? moment(start_date).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                end_date: end_date ? moment(end_date).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                full_name,
                registered_from: registered_from ? moment(registered_from).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                registered_to: registered_to ? moment(registered_to).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                board_approval_from: board_approval_from ? moment(board_approval_from).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                board_approval_to: board_approval_to ? moment(board_approval_to).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                technology_field,
                identity_number,
                payment_from: payment_from ? moment(payment_from).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                payment_to: payment_to ? moment(payment_to).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                assessment_from: assessment_from ? moment(assessment_from).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                assessment_to: assessment_to ? moment(assessment_to).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : null,
                search,
                status,
                email,
                filter_status,
                registration_no,
            });
            return response?.data?.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error.response.data); // Reject with error message
        }
    }
);

export const fetchIncompleteApplicationsProfileInformation = createAsyncThunk("applicants/fetchIncompleteApplicationsProfileInformation", async ({ id }, thunkAPI) => {
    try {
        const response = await incompleteApplicationsService.getIncompleteApplicationssProfileInformation({ id });
        return response?.data?.data;
    } catch (error) {
        return thunkAPI.rejectWithValue(error.response.data);
    }
});

// slice
const incompleteApplicationsSlice = createSlice({
    name: "incompleteApplications",
    initialState,
     reducers: {
            handleLoading: (state, action) => {
                state.loading = action.payload;
            },
            handleUserId: (state, action) => {
                state.userId = action.payload;
            },
            handleCertificateStatus: (state, action) => {
                state.certificateStatus = action.payload;
            },
            handleCertificateTSTCStatus: (state, action) => {
                state.certificateTSTCStatus = action.payload;
            },
            handleBulkRegistrationStatus: (state, action) => {
                state.bulkRegistrationStatus = action.payload;
            },
            handleCertificateType: (state, action) => {
                state.certificateType = action.payload;
            },
            handleProfileInformationType: (state, action) => {
                state.profileDetailsType = action.payload;
            },
            emptyProfileInformationData: (state, action) => {
                state.profileData = {};
            },
            handleCurrentPage: (state, action) => {
                state.data.current_page = action.payload;
            },
            setStartDate: (state, action) => {
                state.startDate = action.payload;
            },
            setEndDate: (state, action) => {
                state.endDate = action.payload;
            },
            setSearch: (state, action) => {
                state.search = action.payload;
            },
            handleGetApplicationStatus: (state, action) => {
                state.applicationStatus = action.payload;
            },
            handleResetApplicationStatus: (state, action) => {
                state.applicationStatus = initialState.applicationStatus;
            },
            logoutnewapplicant: (state, action) => {
                return initialState;
            },
            resetData: (state, action) => {
                state.data = {
                    current_page: 1,
                    page_limit: 10,
                    total_pages: 1,
                };
                state.search = "";
            },
            updateProfileDataStatus: (state, action) => {
                const { id, status } = action.payload;
                state.profileData.status = status;
            },
        },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIncompleteApplicantions.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchIncompleteApplicantions.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
                if (action.payload.total_pages === 0) {
                    state.data.total_pages = 1;
                } else {
                    state.data.total_pages = action.payload.total_pages;
                }
            })
            .addCase(fetchIncompleteApplicantions.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchIncompleteApplicationsProfileInformation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchIncompleteApplicationsProfileInformation.fulfilled, (state, action) => {
                state.loading = false;
                state.profileData = { ...action.payload };
            })
            .addCase(fetchIncompleteApplicationsProfileInformation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    handleLoading,
    handleSelfCheckTableDetails,
    handleCertificateStatus,
    handleCertificateType,
    handleProfileInformationType,
    emptyProfileInformationData,
    handleCurrentPage,
    setStartDate,
    setEndDate,
    setSearch,
    logoutnewapplicant,
    handleBulkRegistrationStatus,
    handleGetApplicationStatus,
    handleResetApplicationStatus,
    resetData,
    handleCertificateTSTCStatus,
    handleUserId,
    updateProfileDataStatus
} = incompleteApplicationsSlice.actions;

export default incompleteApplicationsSlice.reducer;
